import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  DOCUMENT,
  effect,
  inject,
  OnInit,
  Signal,
  viewChild,
  ViewContainerRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '@env/environment';
import { SentryService } from '@daisi/services/sentry/sentry.service';
import { AgGridService } from '@daisi/services/ag-grid/ag-grid.service';
import { PlausibleService } from '@daisi/services/plausible.service';
import { UserStateService } from '@daisi/services/user-state/user-state.service';
import { CustomModalService } from '@daisi/services/custom-modal/custom-modal.service';
import { AuthService } from '@services/auth/auth.service';
import { AudioNotificationService } from '@services/audio-notification.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { PushNotificationsService } from '@services/push-notifications.service';
import { AppUpdateService } from '@services/app-update.service';
import { QueryParamService } from '@services/query-param.service';
import { TimeHelper } from '@shared/helpers/time.helper';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, AfterViewInit {
  readonly modalContainer: Signal<ViewContainerRef> = viewChild.required('modalContainer', {
    read: ViewContainerRef,
  });
  private readonly sentryService = inject(SentryService);
  private readonly agGridService = inject(AgGridService);
  private readonly router = inject(Router);
  private readonly plausibleService = inject(PlausibleService);
  private readonly userStateService = inject(UserStateService);
  private readonly modalService = inject(CustomModalService);
  private readonly authService = inject(AuthService);
  private readonly audioNotificationService = inject(AudioNotificationService);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly destroyRef = inject(DestroyRef);
  private document = inject(DOCUMENT);
  private appUpdateService = inject(AppUpdateService);
  private timeHelper = inject(TimeHelper);

  // inject to load the effects:
  private queryParamService = inject(QueryParamService);
  private pushNotificationsService = inject(PushNotificationsService);

  readonly faviconIcon = computed(() => {
    return this.audioNotificationService.hasUnreadNotification()
      ? 'favicon-with-sound.ico'
      : 'favicon.ico';
  });

  constructor() {
    this.appUpdateService.listenForUpdates();
    this.timeHelper.initialize();

    effect(() => {
      this.updateFavicon(this.faviconIcon());
    });

    this.breakpointObserver
      .observe(['(max-width: 1024px)'])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.userStateService.state.isMobile.set(result.matches);
      });
  }

  ngOnInit(): void {
    this.sentryService.initialize(environment.sentry);

    this.router.events.subscribe((event): void => {
      if (event instanceof NavigationEnd) {
        this.plausibleService.trackPageview();
      }
    });

    const userId: string | undefined = this.authService.getUser()?.userId;
    if (!userId) {
      return;
    }
  }

  ngAfterViewInit(): void {
    if (this.modalContainer() !== undefined) {
      this.modalService.setViewContainerRef(this.modalContainer());
    }
  }

  private updateFavicon(iconUrl: string) {
    const link = this.document.querySelector("link[rel*='icon']") as HTMLLinkElement;
    if (link) {
      link.href = iconUrl;
    }
  }
}
